import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, X, Clock, MapPin, Target, Gauge, MessageSquare, Sparkles, SlidersHorizontal, Lock, Crown, ArrowRight, Snowflake, ShieldCheck, Users, Brain } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/app/AppShell";
import MatchingGate from "@/components/app/MatchingGate";
import AdSlot from "@/components/app/AdSlot";
import { ADSENSE_SLOTS } from "@/config/adsense";
import { IconAvatar } from "@/components/app/IconAvatar";
import StreakIcon from "@/components/app/StreakIcon";
import { getDiscoverDeck, likeDiscoverProfile, passDiscoverProfile } from "@/lib/api";
import { getProfile } from "@/lib/storage";
import { SearchableMultiSelectField, SliderField, SwitchField } from "@/onboarding/fields";
import { CITY_OPTIONS, COUNTRY_OPTIONS, TIMEZONE_OPTIONS } from "@/onboarding/locationOptions";
import { CHRONOTYPES, COMMITMENT_DURATIONS, GENDERS, PARTNERSHIP_INTENTS, partnershipIntentLabel } from "@/onboarding/data";

const LANGUAGE_OPTIONS = ["English", "Hindi", "Spanish", "Mandarin", "French", "German", "Japanese", "Arabic", "Portuguese", "Tamil"];
const PRO_UPGRADE_URL = process.env.REACT_APP_PRO_UPGRADE_URL || "/#pricing";

const emptyFilters = () => ({
  useProfilePreferences: true,
  timezones: [],
  cities: [],
  countries: [],
  genders: [],
  chronotypes: [],
  commitmentDurations: [],
  partnershipIntents: [],
  subscriptionTiers: [],
  languages: [],
  minAge: "",
  maxAge: "",
  minDailyHours: 0,
  minCommitmentHours: "",
  maxTimezoneDiff: "",
  minOverlapScore: 0,
});

export default function Discover() {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [swipeDir, setSwipeDir] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [successfulDecisions, setSuccessfulDecisions] = useState(0);
  const [showDiscoverAd, setShowDiscoverAd] = useState(false);
  const [entitlements, setEntitlements] = useState(null);
  const [likesRemaining, setLikesRemaining] = useState(null);
  const [poolStatus, setPoolStatus] = useState({
    viewerTier: null,
    tierPoolExhausted: false,
    upgradeAvailableProfiles: 0,
  });
  const soloMode = getProfile()?.usageMode === "solo";

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (soloMode) {
        if (!cancelled) setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const deck = await getDiscoverDeck(sanitizeFilters(filters));
        if (!cancelled) {
          setCandidates(deck.candidates || []);
          setEntitlements(deck.entitlements || null);
          setLikesRemaining(deck.likesRemainingToday);
          setPoolStatus({
            viewerTier: deck.viewerTier,
            tierPoolExhausted: Boolean(deck.tierPoolExhausted),
            upgradeAvailableProfiles: deck.upgradeAvailableProfiles || 0,
          });
          setIdx(0);
        }
      } catch (error) {
        toast("Could not load discover profiles.", { description: error.message });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [filters, soloMode]);

  const current = showDiscoverAd ? null : candidates[idx];
  const next = candidates[idx + 1];
  const compatibility = current?.fitScore ?? 0;
  const isFreePoolExhausted = poolStatus.viewerTier === "free" && poolStatus.tierPoolExhausted;
  const hasMatchCapacity = entitlements?.hasMatchCapacity ?? entitlements?.has_match_capacity ?? true;
  const showExpandedProfiles = poolStatus.viewerTier === "pro" || poolStatus.viewerTier === "winter_arc";

  const updateFilter = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
  const resetFilters = () => setFilters(emptyFilters());

  const decide = (dir) => {
    if (!current || swipeDir) return;
    if (dir === "right" && !hasMatchCapacity) {
      const paid = poolStatus.viewerTier === "pro" || poolStatus.viewerTier === "winter_arc";
      toast("No new matches remaining.", {
        description: paid
          ? "Add another Pro match pack before sending more likes."
          : "Your Free match is already in use. Upgrade to make another match.",
      });
      return;
    }
    const target = current.userId;
    setSwipeDir(dir);
    setTimeout(async () => {
      try {
        if (dir === "right") {
          const result = await likeDiscoverProfile(target);
          setLikesRemaining(result.likesRemainingToday);
          if (result.hasMatchCapacity != null) {
            setEntitlements((currentEntitlements) => ({
              ...currentEntitlements,
              hasMatchCapacity: result.hasMatchCapacity,
            }));
          }
          if (result.matched) {
            toast(`It's a match with ${current.profile.fullName.split(" ")[0]}.`, { description: "Both of you said yes." });
          } else {
            toast("Added to your Liked list.", { description: "You'll match if they like you back." });
          }
        } else {
          await passDiscoverProfile(target);
        }
        const remainingCandidates = candidates.filter((item) => item.userId !== target);
        setCandidates(remainingCandidates);
        setSuccessfulDecisions((count) => {
          const nextCount = count + 1;
          const frequency = entitlements?.discoverAdFrequency ?? entitlements?.discover_ad_frequency ?? 2;
          if (nextCount % frequency === 0) setShowDiscoverAd(true);
          return nextCount;
        });

        if (remainingCandidates.length === 0) {
          setLoading(true);
          try {
            const deck = await getDiscoverDeck(sanitizeFilters(filters));
            setCandidates(deck.candidates || []);
            setEntitlements(deck.entitlements || null);
            setLikesRemaining(deck.likesRemainingToday);
            setPoolStatus({
              viewerTier: deck.viewerTier,
              tierPoolExhausted: Boolean(deck.tierPoolExhausted),
              upgradeAvailableProfiles: deck.upgradeAvailableProfiles || 0,
            });
            setIdx(0);
          } catch (refreshError) {
            toast("Your decision was saved, but Discover could not refresh.", { description: refreshError.message });
          } finally {
            setLoading(false);
          }
        }
      } catch (error) {
        toast("Could not save your decision.", { description: error.message });
      } finally {
        setSwipeDir(null);
      }
    }, 350);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && filtersOpen) {
        setFiltersOpen(false);
        return;
      }
      if (filtersOpen) return;
      if (e.key === "ArrowLeft") decide("left");
      if (e.key === "ArrowRight") decide("right");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, swipeDir, filtersOpen]);

  return (
    <AppShell>
      <MatchingGate>
      <div className="px-6 sm:px-10 py-10 max-w-5xl mx-auto">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="overline mb-3">Discover</div>
            <h1 className="font-serif-editorial font-light tracking-tighter text-4xl sm:text-5xl leading-[1.05]">
              Curated candidates.
            </h1>
            <p className="mt-3 text-[#A1A1AA] font-light max-w-xl">
              Paid members can discover every tier. Free members can connect with paid members who like them first.
            </p>
          </div>
          <button
            onClick={() => entitlements?.discoverFilters || entitlements?.discover_filters ? setFiltersOpen((v) => !v) : toast("Filters are a Pro feature.", { description: "Free members still get unlimited profile browsing." })}
            data-testid="disc-filters-toggle"
            aria-expanded={filtersOpen}
            aria-controls="discover-filters-panel"
            className="hm-btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs"
          >
            {entitlements && !(entitlements.discoverFilters ?? entitlements.discover_filters) ? <Lock className="w-3.5 h-3.5" /> : <SlidersHorizontal className="w-3.5 h-3.5" />} Filters
          </button>
        </div>

        {likesRemaining != null && (
          <div className="mb-5 text-xs text-[#A1A1AA]">
            <span className="text-[#F59E0B]">{likesRemaining}</span> likes remaining today · unlimited profile browsing
            {entitlements?.matchCreditsRemaining != null && <> · <span className="text-[#F59E0B]">{entitlements.matchCreditsRemaining}</span> new matches remaining</>}
          </div>
        )}

        <div className="relative min-h-[560px] flex items-start justify-center">
          {filtersOpen && (entitlements?.discoverFilters ?? entitlements?.discover_filters) && (
            <div className="absolute inset-0 z-40 flex items-start justify-center" data-testid="disc-filters-overlay">
              <button className="absolute inset-0 cursor-default bg-black/70 backdrop-blur-[2px]" onClick={() => setFiltersOpen(false)} aria-label="Close filters" />
              <DiscoverFilters filters={filters} update={updateFilter} reset={resetFilters} onClose={() => setFiltersOpen(false)} />
            </div>
          )}
          {loading && (
            <div className="hm-card p-10 rounded-md text-center max-w-lg">
              <Sparkles className="w-8 h-8 text-[#F59E0B] mx-auto mb-4" strokeWidth={1.25} />
              <h2 className="font-serif-editorial text-3xl mb-3">Loading candidates...</h2>
            </div>
          )}

          {!loading && !current && !showDiscoverAd && (
            isFreePoolExhausted ? (
              <TierPoolExhaustedCard
                availableProfiles={poolStatus.upgradeAvailableProfiles}
                onUpgrade={() => window.location.assign(PRO_UPGRADE_URL)}
                onMatches={() => navigate("/matches")}
              />
            ) : (
              <div className="hm-card p-10 rounded-md text-center max-w-lg">
                <Sparkles className="w-8 h-8 text-[#F59E0B] mx-auto mb-4" strokeWidth={1.25} />
                <h2 className="font-serif-editorial text-3xl mb-3">No candidates found.</h2>
                <p className="text-[#A1A1AA] font-light mb-6">
                  Loosen your filters or check your matches while the pool grows.
                </p>
                <div className="flex gap-3 justify-center">
                  <button onClick={resetFilters} className="hm-btn-ghost px-4 py-2 rounded-md text-sm">Reset filters</button>
                  <button onClick={() => navigate("/matches")} className="hm-btn-primary px-4 py-2 rounded-md text-sm font-semibold">See matches</button>
                </div>
              </div>
            )
          )}
          {next && current && (
            <div className="absolute inset-0 flex justify-center pointer-events-none">
              <div className="w-full max-w-xl scale-[0.96] opacity-60 translate-y-4">
                <ProfileCard profile={next.profile} compact subscriptionTier={next.subscriptionTier} winterArcScore={next.winterArcScore} winterArcStreak={next.winterArcStreak} consistencyScore={next.consistencyScore} consistencyStreak={next.consistencyStreak} totalCompletions={next.totalCompletions} verifiedPercent={next.verifiedPercent} />
              </div>
            </div>
          )}

          {showDiscoverAd && (
            <DiscoverAdCard onContinue={() => setShowDiscoverAd(false)} />
          )}

          {current && (
            <div
              className={`relative w-full max-w-xl transition-transform duration-300 ease-out ${
                swipeDir === "left" ? "-translate-x-[120%] -rotate-6 opacity-0" :
                swipeDir === "right" ? "translate-x-[120%] rotate-6 opacity-0" : ""
              }`}
              data-testid="disc-current-card"
            >
              <ProfileCard profile={current.profile} compatibility={compatibility} expanded={showExpandedProfiles} subscriptionTier={current.subscriptionTier} winterArcScore={current.winterArcScore} winterArcStreak={current.winterArcStreak} consistencyScore={current.consistencyScore} consistencyStreak={current.consistencyStreak} totalCompletions={current.totalCompletions} verifiedPercent={current.verifiedPercent} overlapScore={current.overlapScore} sharedMinutesWeek={current.sharedMinutesWeek} showOverlap={showExpandedProfiles} />
            </div>
          )}
        </div>

        {current && (
          <div className="flex items-center justify-center gap-6 mt-8">
            <button onClick={() => decide("left")} data-testid="disc-pass" className="w-14 h-14 rounded-full border border-white/10 bg-[#0f0f0f] text-[#A1A1AA] hover:text-[#F5F5F0] hover:border-white/20 transition-all inline-flex items-center justify-center hover:-translate-y-0.5" title="Pass">
              <X className="w-5 h-5" strokeWidth={1.75} />
            </button>
            <button onClick={() => decide("right")} disabled={!hasMatchCapacity} data-testid="disc-like" className="w-16 h-16 rounded-full bg-[#F59E0B] text-black hover:bg-[#D97706] transition-all inline-flex items-center justify-center hover:-translate-y-0.5 amber-glow disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0" title={hasMatchCapacity ? "Like" : "No new matches remaining"}>
              <Heart className="w-6 h-6" fill="currentColor" strokeWidth={1.5} />
            </button>
          </div>
        )}
      </div>
      </MatchingGate>
    </AppShell>
  );
}

function sanitizeFilters(filters) {
  return {
    ...filters,
    minAge: filters.minAge || undefined,
    maxAge: filters.maxAge || undefined,
    minDailyHours: filters.minDailyHours > 0 ? filters.minDailyHours : undefined,
    minCommitmentHours: filters.minCommitmentHours || undefined,
    maxTimezoneDiff: filters.maxTimezoneDiff || undefined,
    minOverlapScore: filters.minOverlapScore > 0 ? filters.minOverlapScore : undefined,
  };
}

function DiscoverFilters({ filters, update, reset, onClose }) {
  return (
    <div id="discover-filters-panel" className="relative z-10 max-h-[min(72vh,680px)] w-full max-w-3xl overflow-y-auto rounded-md border border-white/15 bg-[#090909] p-5 shadow-2xl sm:p-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="overline mb-2">Discover filters</div>
          <p className="text-xs text-[#A1A1AA]">Refine the candidate pool without leaving the current profile.</p>
        </div>
        <button onClick={onClose} className="hm-btn-ghost inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md" aria-label="Close filters"><X className="h-4 w-4" /></button>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <SwitchField label="Use my onboarding partner preferences" hint="Age range, preferred languages, commitment, gender, and timezone difference." value={filters.useProfilePreferences} onChange={(v) => update({ useProfilePreferences: v })} />
        <div className="grid grid-cols-2 gap-3">
          <NumberInput label="Min age" value={filters.minAge} onChange={(v) => update({ minAge: v })} min={18} max={100} />
          <NumberInput label="Max age" value={filters.maxAge} onChange={(v) => update({ maxAge: v })} min={18} max={100} />
        </div>
        <SearchableMultiSelectField label="Cities" value={filters.cities} onChange={(v) => update({ cities: v })} options={CITY_OPTIONS} placeholder="Search city" />
        <SearchableMultiSelectField label="Countries" value={filters.countries} onChange={(v) => update({ countries: v })} options={COUNTRY_OPTIONS} placeholder="Search country" />
        <SearchableMultiSelectField label="Timezones" value={filters.timezones} onChange={(v) => update({ timezones: v })} options={TIMEZONE_OPTIONS} placeholder="Search timezone" />
        <SearchableMultiSelectField label="Languages" value={filters.languages} onChange={(v) => update({ languages: v })} options={LANGUAGE_OPTIONS} placeholder="Search language" />
        <SearchableMultiSelectField label="Gender" value={filters.genders} onChange={(v) => update({ genders: v })} options={GENDERS.filter((g) => g !== "Custom")} placeholder="Search gender" />
        <SearchableMultiSelectField label="Chronotype" value={filters.chronotypes} onChange={(v) => update({ chronotypes: v })} options={CHRONOTYPES.map((c) => c.key)} placeholder="Search chronotype" allowCustom={false} />
        <SearchableMultiSelectField label="Minimum commitment duration" value={filters.commitmentDurations} onChange={(v) => update({ commitmentDurations: v })} options={COMMITMENT_DURATIONS} placeholder="Search duration" allowCustom={false} />
        <IntentFilter value={filters.partnershipIntents} onChange={(v) => update({ partnershipIntents: v })} />
        <TierFilter value={filters.subscriptionTiers} onChange={(v) => update({ subscriptionTiers: v })} />
        <div className="grid grid-cols-2 gap-3">
          <NumberInput label="Min commitment hours" value={filters.minCommitmentHours} onChange={(v) => update({ minCommitmentHours: v })} min={0} max={16} />
          <NumberInput label="Max timezone diff" value={filters.maxTimezoneDiff} onChange={(v) => update({ maxTimezoneDiff: v })} min={0} max={12} />
        </div>
      </div>
      <div className="mt-6">
        <SliderField label="Minimum daily work hours" min={0} max={16} value={filters.minDailyHours} onChange={(v) => update({ minDailyHours: v })} leftLabel="Any" rightLabel="16h" format={(v) => `${v}h+`} />
        <div className="mt-5"><SliderField label="Minimum free-time overlap" min={0} max={100} value={filters.minOverlapScore} onChange={(v) => update({ minOverlapScore: v })} leftLabel="Any" rightLabel="100" format={(v) => v === 0 ? "Any" : `${v}+`} /><p className="mt-1 text-xs text-[#777]">Profiles without enough schedule data are excluded when this filter is on.</p></div>
      </div>
      <button onClick={reset} className="hm-btn-ghost mt-5 px-4 py-2 rounded-md text-xs">Reset filters</button>
    </div>
  );
}

function IntentFilter({ value = [], onChange }) {
  const toggle = (key) => onChange(value.includes(key) ? value.filter((item) => item !== key) : [...value, key]);
  return (
    <div className="md:col-span-2">
      <div className="mb-3 text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">Connection intent</div>
      <div className="grid gap-2 sm:grid-cols-2">
        {PARTNERSHIP_INTENTS.map((option) => {
          const active = value.includes(option.key);
          return <button key={option.key} type="button" onClick={() => toggle(option.key)} className={`rounded-md border px-3 py-2 text-left text-xs ${active ? "border-[#F59E0B] bg-[#F59E0B]/10 text-[#F5F5F0]" : "border-white/10 bg-[#0f0f0f] text-[#A1A1AA]"}`}>{option.label}</button>;
        })}
      </div>
    </div>
  );
}

const SUBSCRIPTION_TIERS = [
  { key: "free", label: "Free" },
  { key: "pro", label: "Pro" },
  { key: "winter_arc", label: "Winter Arc" },
];

function TierFilter({ value = [], onChange }) {
  const toggle = (key) => onChange(value.includes(key) ? value.filter((item) => item !== key) : [...value, key]);
  return (
    <div className="md:col-span-2">
      <div className="mb-3 text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">Subscription tier</div>
      <div className="grid grid-cols-3 gap-2">
        {SUBSCRIPTION_TIERS.map((option) => {
          const active = value.includes(option.key);
          return <button key={option.key} type="button" onClick={() => toggle(option.key)} className={`rounded-md border px-3 py-2 text-left text-xs ${active ? "border-[#F59E0B] bg-[#F59E0B]/10 text-[#F5F5F0]" : "border-white/10 bg-[#0f0f0f] text-[#A1A1AA]"}`}>{option.label}</button>;
        })}
      </div>
    </div>
  );
}

function NumberInput({ label, value, onChange, min, max }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">{label}</span>
      <input type="number" min={min} max={max} value={value} onChange={(e) => onChange(e.target.value)} className="hm-input w-full px-3 py-3 rounded-md text-sm" />
    </label>
  );
}

function ProfileCard({ profile, compatibility, compact = false, expanded = false, subscriptionTier, winterArcScore, winterArcStreak, consistencyScore = 0, consistencyStreak = 0, totalCompletions = 0, verifiedPercent = 0, overlapScore = null, sharedMinutesWeek = null, showOverlap = false }) {
  const nonNeg = (profile.idealDay || []).filter((b) => (profile.nonNegotiableBlocks || []).includes(b.id));
  return (
    <div className="hm-card rounded-md overflow-hidden">
      <div className="relative p-8 pb-6 border-b border-white/5">
        <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-[#F59E0B]/10 blur-2xl pointer-events-none" />
        <div className="flex items-start gap-5">
          <IconAvatar iconKey={profile.profileIcon} size="xl" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="font-serif-editorial text-3xl leading-tight truncate">{profile.fullName}</h2>
              {compatibility != null && (
                <span className="ml-auto shrink-0 text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]">
                  {compatibility}% fit
                </span>
              )}
            </div>
            <div className="text-xs text-[#A1A1AA] flex flex-wrap items-center gap-x-3 gap-y-1 mb-3">
              <span>@{profile.username}</span>
              <span>{profile.age}</span>
              <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {[profile.city, profile.country].filter(Boolean).join(", ")}</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {profile.timezone}</span>
            </div>
            <p className="text-sm text-[#F5F5F0] font-light leading-relaxed line-clamp-3">{profile.bio}</p>
            {profile.partnershipIntent && <div className="mt-3 text-[10px] uppercase tracking-[.16em] text-[#A1A1AA]">Looking for · <span className="text-[#F5F5F0]">{partnershipIntentLabel(profile.partnershipIntent)}</span></div>}
            <div className="mt-3 flex flex-wrap gap-2 text-[9px] uppercase tracking-[.14em]">
              {overlapScore != null && <span className="inline-flex items-center gap-1.5 border border-sky-300/30 bg-sky-300/[.07] px-2.5 py-1 text-sky-100" title="Shared free time from both schedules over the next seven days; 100 means at least 14 hours, not guaranteed availability"><Clock className="h-3 w-3" /> {overlapScore} overlap · {Math.round(sharedMinutesWeek / 60 * 10) / 10}h/wk</span>}
              {overlapScore == null && showOverlap && <span className="inline-flex items-center gap-1.5 border border-white/10 px-2.5 py-1 text-[#777]" title="Both members need complete ideal-day, availability, working-day, and timezone details">Overlap unavailable</span>}
              <span className="inline-flex items-center gap-1.5 border border-white/15 bg-white/[.06] px-2.5 py-1 text-[#F5F5F0]" title="30-day consistency based on all recurring task completions, including solo progress">
                <StreakIcon className="w-3 h-3" strokeWidth={2} lit={consistencyStreak > 0} /> {consistencyScore}% consistency · {consistencyStreak}d streak
              </span>
              <span className={`inline-flex items-center gap-1.5 border px-2.5 py-1 ${verifiedPercent > 0 ? "border-emerald-400/35 bg-emerald-400/10 text-emerald-200" : "border-white/15 bg-white/[.03] text-[#A1A1AA]"}`} title="Share of task completions confirmed through a partnership">
                <ShieldCheck className="w-3 h-3" /> {totalCompletions > 0 ? `${verifiedPercent}% verified` : "No history yet"}
              </span>
            </div>
            {subscriptionTier === "winter_arc" && <div className="mt-3 inline-flex items-center gap-2 border border-white/25 bg-black px-2.5 py-1 text-[9px] uppercase tracking-[.18em] text-white"><Snowflake className="w-3 h-3" />Winter Arc · {winterArcScore}% · {winterArcStreak}d streak</div>}
          </div>
        </div>
      </div>
      {!compact && (
        <div className="p-8 pt-6">
          <div className="grid sm:grid-cols-2 gap-5">
            <Row icon={Target} label="Primary goal">
              <div className="text-sm text-[#F5F5F0] mb-2">{profile.goalDescription}</div>
              <TagList items={profile.goalTags} accent />
              <div className="text-xs text-[#A1A1AA] mt-3">
                Target: <span className="text-[#F5F5F0]">{profile.target || "Not specified"}</span>{profile.targetDate && <> by {profile.targetDate}</>}
              </div>
            </Row>
            <Row icon={Gauge} label="Operating style">
              <ul className="space-y-1.5 text-sm">
                <RowLine k="Chronotype" v={cap(profile.chronotype)} />
                <RowLine k="Focus" v={`${profile.dailyHours}h / day ${profile.focusLength || ""}`} />
                <RowLine k="Commit" v={profile.commitmentDuration} />
                <RowLine k="Motivation" v={profile.motivationType} />
                <RowLine k="Strictness" v={`${profile.strictness} / 10`} />
              </ul>
            </Row>
            <Row icon={MessageSquare} label="Non-negotiables">
              {nonNeg.length === 0 ? (
                <div className="text-xs text-[#A1A1AA] italic">Not specified</div>
              ) : (
                <ul className="space-y-1 text-sm">
                  {nonNeg.map((b) => (
                    <li key={b.id} className="text-[#F5F5F0] font-light">
                      <span className="font-serif-editorial text-[#F59E0B] mr-2">{b.start}-{b.end}</span>{b.label}
                    </li>
                  ))}
                </ul>
              )}
            </Row>
            <Row icon={Sparkles} label="What they want">
              <div className="text-sm text-[#F5F5F0] font-light line-clamp-3">{profile.partnerDescription || "-"}</div>
              <div className="mt-3"><TagList items={(profile.interests || []).slice(0, 5)} /></div>
            </Row>
          </div>
          {expanded && <ExpandedProfileDetails profile={profile} />}
        </div>
      )}
    </div>
  );
}

function ExpandedProfileDetails({ profile }) {
  const goals = profile.goals || [];
  const primaryIndex = Math.max(0, goals.findIndex((goal) => goal.isPrimary));
  const otherGoals = goals.filter((_, index) => index !== primaryIndex);
  const ageRange = profile.ageRange?.length === 2 ? `${profile.ageRange[0]}–${profile.ageRange[1]}` : null;

  return (
    <div className="mt-7 border-t border-white/10 pt-7" data-testid="disc-expanded-profile">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <div className="overline text-[#F59E0B]">Detailed profile</div>
          <p className="mt-1 text-xs text-[#A1A1AA]">Paid-member context for making a more intentional decision.</p>
        </div>
        <Crown className="h-4 w-4 shrink-0 text-[#F59E0B]" />
      </div>

      <div className="space-y-7">
        <DetailSection icon={Target} title="Other goals">
          {otherGoals.length ? (
            <div className="space-y-3">
              {otherGoals.map((goal, index) => (
                <div key={goal.id || index} className="border border-white/10 bg-white/[.025] p-4">
                  <div className="mb-2 flex items-start justify-between gap-3">
                    <TagList items={[...(goal.tags || []), goal.customGoal].filter(Boolean)} accent />
                    <span className="shrink-0 text-[10px] uppercase tracking-[.16em] text-[#A1A1AA]">{goal.importance || 7}/10 priority</span>
                  </div>
                  <p className="text-sm font-light leading-relaxed text-[#F5F5F0]">{goal.description || "No description provided."}</p>
                  {(goal.target || goal.targetDate) && (
                    <p className="mt-2 text-xs text-[#A1A1AA]">Target: <span className="text-[#F5F5F0]">{goal.target || "Milestone"}</span>{goal.targetDate && <> by {goal.targetDate}</>}</p>
                  )}
                </div>
              ))}
            </div>
          ) : <EmptyDetail>No additional goals listed.</EmptyDetail>}
        </DetailSection>

        <DetailSection icon={Users} title="Partner preferences">
          <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            <DetailStat label="Looking for" value={profile.lookingFor} />
            <DetailStat label="Preferred level" value={profile.partnerLevel} />
            <DetailStat label="Preferred age" value={ageRange} />
            <DetailStat label="Preferred gender" value={profile.partnerGender} />
            <DetailStat label="Languages" value={(profile.languagesPreferred || []).join(", ")} />
            <DetailStat label="Minimum commitment" value={profile.minCommitment ? `${profile.minCommitment}h daily` : null} />
            <DetailStat label="Timezone range" value={profile.maxTimezoneDiff != null ? `±${profile.maxTimezoneDiff}h` : null} />
            <DetailStat label="Shared goals" value={profile.sharedGoals ? "Open to it" : "Not preferred"} />
          </div>
          {(profile.dealBreakers || []).length > 0 && (
            <div className="mt-4">
              <div className="mb-2 text-[10px] uppercase tracking-[.18em] text-[#A1A1AA]">Deal breakers</div>
              <TagList items={profile.dealBreakers} />
            </div>
          )}
        </DetailSection>

        <DetailSection icon={Brain} title="Personality">
          <PersonalityScales profile={profile} />
          <div className="mt-5">
            <div className="mb-2 text-[10px] uppercase tracking-[.18em] text-[#A1A1AA]">Interests</div>
            <TagList items={profile.interests} />
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {profile.quote && <Callout label="Their line">“{profile.quote}”</Callout>}
            {profile.funFact && <Callout label="Fun fact">{profile.funFact}</Callout>}
          </div>
        </DetailSection>
      </div>
    </div>
  );
}

function DetailSection({ icon: Icon, title, children }) {
  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-[#F59E0B]" strokeWidth={1.5} />
        <h3 className="text-xs uppercase tracking-[.2em] text-[#F5F5F0]">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function PersonalityScales({ profile }) {
  const scales = [
    ["introExtro", "Introvert", "Extrovert"],
    ["plannerSpont", "Planner", "Spontaneous"],
    ["compCoop", "Competitive", "Cooperative"],
    ["seriousFun", "Serious", "Fun"],
    ["optRealist", "Optimistic", "Realistic"],
    ["logicEmo", "Logical", "Emotional"],
  ];
  return (
    <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
      {scales.map(([key, left, right]) => {
        const value = Math.min(10, Math.max(1, Number(profile[key]) || 5));
        return (
          <div key={key}>
            <div className="flex justify-between text-[10px] text-[#A1A1AA]"><span>{left}</span><span>{right}</span></div>
            <div className="relative mt-1.5 h-1 rounded-full bg-white/10">
              <div className="absolute -top-1 h-3 w-3 rounded-full bg-[#F59E0B]" style={{ left: `calc(${((value - 1) / 9) * 100}% - 6px)` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DetailStat({ label, value }) {
  if (value == null || value === "") return null;
  return <div className="grid grid-cols-[120px_1fr] gap-3 text-xs"><span className="text-[#A1A1AA]">{label}</span><span className="text-[#F5F5F0]">{value}</span></div>;
}

function TagList({ items = [], accent = false }) {
  const values = items.filter(Boolean);
  if (!values.length) return <span className="text-xs italic text-[#A1A1AA]">Not specified</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {values.map((item, index) => (
        <span key={`${item}-${index}`} className={`px-2 py-0.5 text-[10px] uppercase tracking-[.16em] border ${accent ? "border-[#F59E0B]/30 bg-[#F59E0B]/5 text-[#F59E0B]" : "border-white/10 text-[#A1A1AA]"}`}>{item}</span>
      ))}
    </div>
  );
}

function Callout({ label, children }) {
  return <div className="border-l border-[#F59E0B]/50 bg-white/[.025] px-4 py-3"><div className="mb-1 text-[9px] uppercase tracking-[.18em] text-[#A1A1AA]">{label}</div><div className="text-sm font-light text-[#F5F5F0]">{children}</div></div>;
}

function EmptyDetail({ children }) {
  return <p className="text-xs italic text-[#A1A1AA]">{children}</p>;
}

function Row({ icon: Icon, label, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5 text-[#F59E0B]" strokeWidth={1.5} />
        <div className="overline">{label}</div>
      </div>
      <div className="pl-5">{children}</div>
    </div>
  );
}

function RowLine({ k, v }) {
  return <li className="text-[#F5F5F0] font-light"><span className="text-[#A1A1AA]">{k}:</span> {v || "-"}</li>;
}

function cap(s) {
  if (!s) return "-";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function TierPoolExhaustedCard({ availableProfiles, onUpgrade, onMatches }) {
  const profileCopy = availableProfiles > 0
    ? `${availableProfiles} paid ${availableProfiles === 1 ? "profile can" : "profiles can"} still discover and like you.`
    : "Upgrade to discover and initiate connections with every tier.";

  return (
    <div className="hm-card p-10 rounded-md text-center max-w-lg border border-[#F59E0B]/25">
      <div className="w-12 h-12 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/25 inline-flex items-center justify-center mb-5">
        <Crown className="w-6 h-6 text-[#F59E0B]" strokeWidth={1.5} />
      </div>
      <div className="overline text-[#F59E0B] mb-3">Free pool complete</div>
      <h2 className="font-serif-editorial text-3xl mb-3">You've seen every Free member.</h2>
      <p className="text-[#A1A1AA] font-light mb-2">
        Free members browse Free profiles. Paid members can find you, and their incoming like lets you match back.
      </p>
      <p className="text-[#F5F5F0] text-sm mb-7">{profileCopy}</p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button onClick={onUpgrade} className="hm-btn-primary px-5 py-2.5 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2">
          Upgrade to Pro <ArrowRight className="w-4 h-4" />
        </button>
        <button onClick={onMatches} className="hm-btn-ghost px-4 py-2.5 rounded-md text-sm">See matches</button>
      </div>
    </div>
  );
}

function DiscoverAdCard({ onContinue }) {
  return (
    <div className="relative w-full max-w-xl hm-card rounded-md overflow-hidden min-h-[560px] flex flex-col">
      <AdSlot
        slot={ADSENSE_SLOTS.discover}
        className="flex-1 min-h-[500px] px-6 pt-6"
      />
      <div className="p-6 pt-0 flex justify-center">
        <button onClick={onContinue} className="hm-btn-ghost px-4 py-2 rounded-md text-xs">Continue discovering</button>
      </div>
    </div>
  );
}
